import { useCallback, useRef, useState } from "react"
import { Input } from "@ui/shad"
import { cn } from "@ui/utils"
import { Loader2 } from "lucide-react"

import { BaseInlineInput } from "./base"
import { InlineInputProps } from "./props"
import { useInlineEdit } from "./use-inline-edit"

export function InlineDateInput({
  label,
  LabelIcon,
  hideLabel,
  size,
  placeholder = "Select date",
  value: controlledValue,
  defaultValue,
  onChange,
  onSave,
  isSaving,
  disabled,
  inputClasses,
  fixedLabelWidth,
  validator,
  suffix,
}: InlineInputProps<string | null>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  // Format server date (YYYY-MM-DD) to display format (MM/DD/YYYY)
  const formatDisplayDate = useCallback((dateString: string | null) => {
    if (!dateString) return ""
    const [year, month, day] = dateString.split("-")
    if (!year || !month || !day) return dateString
    return `${month}/${day}/${year}`
  }, [])

  // Parse display format (MM/DD/YYYY) to server format (YYYY-MM-DD)
  const parseDisplayDate = useCallback((displayValue: string) => {
    if (!displayValue) return null

    // Remove any non-digits
    const digits = displayValue.replace(/\D/g, "")
    if (digits.length < 8) return displayValue // Return as-is if incomplete

    // Extract parts from the masked value
    const [month, day, year] = displayValue.split("/")
    if (year && month && day && year.length === 4) {
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    }

    return displayValue
  }, [])

  // Apply date masking to input
  const applyDateMask = useCallback((input: string) => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, "")

    let masked = digits
    if (digits.length > 0) {
      // Format as MM/DD/YYYY
      const parts = []
      if (digits.length > 0) parts.push(digits.slice(0, 2))
      if (digits.length > 2) parts.push(digits.slice(2, 4))
      if (digits.length > 4) parts.push(digits.slice(4, 8))
      masked = parts.join("/")
    }

    return masked
  }, [])

  const handleSave = (dateValue: string | null) => {
    if (!dateValue) {
      onSave?.(null)
      return
    }

    const serverFormat = parseDisplayDate(dateValue)
    if (serverFormat) {
      onSave?.(serverFormat)
    }
  }

  const { stringValue, setValue, isEmpty, isSubmitting, handleSubmit, handleKeyDown } = useInlineEdit({
    value: controlledValue,
    defaultValue,
    onChange,
    onSubmit: onSave ? handleSave : undefined,
    validator,
    formatter: formatDisplayDate,
    parser: applyDateMask,
    ref: inputRef,
    resetOnInvalid: true,
    resetOnEmpty: false,
    disableClickOutside: true,
  })
  const showUnfocusedSuffix = Boolean(suffix) && !isFocused && !isEmpty

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
    >
      <div className="relative w-full">
        <Input
          ref={inputRef}
          type="text"
          value={stringValue}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false)
            void handleSubmit()
          }}
          className={cn(
            "hover:bg-muted focus-visible:border-input h-8 w-full cursor-pointer border-1 border-transparent px-2 shadow-none focus-visible:shadow-sm",
            isEmpty ? "text-input-placeholder" : "",
            showUnfocusedSuffix && "text-transparent",
            inputClasses
          )}
          placeholder={placeholder || "MM/DD/YYYY"}
          disabled={disabled}
          maxLength={10}
        />

        {showUnfocusedSuffix && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-2 text-base md:text-sm">
            <span>{stringValue}</span>
            <span className="text-muted-foreground ml-1.5 text-xs whitespace-nowrap">{suffix}</span>
          </div>
        )}

        {(isSaving || isSubmitting) && <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />}
      </div>
    </BaseInlineInput>
  )
}
